import { createContext, PropsWithChildren, useContext, useMemo } from 'react';

import { EditStateContext } from '../../hooks/use-editable-state';
import { EditTableEntry, IdArray, OnEditComponent } from '../../types';

interface EditTableProviderState<T extends EditTableEntry> {
  onAddComponent?: (props: EditStateContext) => void;
  onEditComponent?: (result: OnEditComponent<T>) => void;
  onMoveComponent?: (localId: string, index: number) => void;
  onRemoveComponent?: (localId: IdArray) => void;
  dragHandlerVisible?: (item: T) => boolean;
  dragHandlerClickable?: (item: T) => boolean;
}

const EditTableProviderContext = createContext<EditTableProviderState<unknown>>(
  {}
);

export type EditTableProviderProps<T> = EditTableProviderState<T>;

export function EditTableProvider<T>(
  props: PropsWithChildren<EditTableProviderProps<T>>
) {
  const {
    onAddComponent,
    onEditComponent,
    onMoveComponent,
    onRemoveComponent,
    dragHandlerVisible,
    dragHandlerClickable,
  } = props;
  const value = useMemo(() => {
    return {
      onAddComponent,
      onEditComponent,
      onMoveComponent,
      onRemoveComponent,
      dragHandlerVisible,
      dragHandlerClickable,
    };
  }, [
    onAddComponent,
    onEditComponent,
    onMoveComponent,
    onRemoveComponent,
    dragHandlerVisible,
    dragHandlerClickable,
  ]);

  return (
    <EditTableProviderContext.Provider value={value}>
      {props.children}
    </EditTableProviderContext.Provider>
  );
}

export const useEditTable = () => {
  const context = useContext(EditTableProviderContext);

  if (context === null)
    console.warn(`useEditTable won't work without <EditTableProvider>`);

  return context;
};
